import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import Select from "react-select";
import PageSizeDropDownSelection from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import "../compnay/ManageCompany.css";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Paid", label: "Paid" },
];

const formatMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusBadgeClass = (status) => {
  const map = {
    Pending: "bg-warning text-dark",
    Approved: "bg-info",
    Paid: "bg-success",
  };
  return map[status] || "bg-secondary";
};

const emptyPayoutForm = {
  marketplaceRider_ID: "",
  amount: "",
  paymentMethod: "",
  paymentReference: "",
  notes: "",
};

const ManageRiderPayouts = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [loading, setLoading] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [riders, setRiders] = useState([]);
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
  const [riderFilter, setRiderFilter] = useState({ value: "", label: "All riders" });
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState(emptyPayoutForm);
  const [markPaidPayout, setMarkPaidPayout] = useState(null);
  const [markPaidRef, setMarkPaidRef] = useState("");
  const [markPaidMethod, setMarkPaidMethod] = useState("");

  const riderOptions = [
    { value: "", label: "All riders" },
    ...riders.map((r) => {
      const id = r.marketplaceRiderId || r.marketplaceRider_ID;
      return {
        value: String(id),
        label: `${r.name || `${r.firstName || ""} ${r.lastName || ""}`.trim()} (#${id})`,
      };
    }),
  ];

  const fetchRiders = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_MARKETPLACE_RIDERS, {
        params: { PageSize: 500, PageNumber: 1, ApprovalStatus: "Approved" },
      });
      if (response.data.status) {
        setRiders(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch {
      // non-blocking
    }
  }, []);

  const fetchPayouts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { PageNumber: pageNumber, PageSize: pageSize.value };
      if (statusFilter?.value) params.Status = statusFilter.value;
      if (riderFilter?.value) params.MarketplaceRider_ID = riderFilter.value;
      const response = await axiosInstance.get(APIs.GET_RIDER_PAYOUTS, { params });
      if (response.data.status) {
        setPayouts(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        showErrorToast(response.data.message || "Failed to load payouts");
      }
    } catch {
      showErrorToast("Failed to load payouts");
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize.value, statusFilter?.value, riderFilter?.value]);

  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  useEffect(() => {
    const handler = setTimeout(() => fetchPayouts(), 300);
    return () => clearTimeout(handler);
  }, [fetchPayouts]);

  useEffect(() => {
    setPageNumber(1);
  }, [statusFilter, riderFilter, pageSize.value]);

  const handleCreate = async () => {
    const riderId = parseInt(payoutForm.marketplaceRider_ID, 10);
    const amount = parseFloat(payoutForm.amount);
    if (!riderId || !amount || amount <= 0) {
      showErrorToast("Select rider and enter a positive amount");
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_CREATE_RIDER_PAYOUT, {
        marketplaceRider_ID: riderId,
        amount,
        paymentMethod: payoutForm.paymentMethod || null,
        paymentReference: payoutForm.paymentReference || null,
        notes: payoutForm.notes || null,
      });
      if (response.data.status) {
        showSuccessToast("Payout created");
        setShowCreateModal(false);
        setPayoutForm(emptyPayoutForm);
        fetchPayouts();
      } else {
        showErrorToast(response.data.message || "Create failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Create failed");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (payoutId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_APPROVE_RIDER_PAYOUT, { payout_ID: payoutId });
      if (response.data.status) {
        showSuccessToast("Payout approved");
        fetchPayouts();
      } else {
        showErrorToast(response.data.message || "Approve failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Approve failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!markPaidPayout) return;
    const payoutId = markPaidPayout.payoutId || markPaidPayout.Payout_ID;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_MARK_RIDER_PAYOUT_PAID, {
        payout_ID: payoutId,
        paymentMethod: markPaidMethod || null,
        paymentReference: markPaidRef || null,
      });
      if (response.data.status) {
        showSuccessToast("Payout marked paid");
        setMarkPaidPayout(null);
        setMarkPaidRef("");
        setMarkPaidMethod("");
        fetchPayouts();
      } else {
        showErrorToast(response.data.message || "Mark paid failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Mark paid failed");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter(STATUS_OPTIONS[0]);
    setRiderFilter({ value: "", label: "All riders" });
  };

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item active">Rider Earnings Payouts</li>
          </ol>
          <h1 className="page-header mb-0">Rider Earnings Payouts</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={() => setShowCreateModal(true)}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Create Payout
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <div className="mb-4">
              <div
                className="card filter-card"
                style={{
                  position: "relative",
                  zIndex: 1,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: `0 4px 20px ${themeColor}40`,
                }}
              >
                <div className="card-body p-3">
                  <div className="row g-3 align-items-center">
                    <div className="col-xl-3 col-lg-4 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={statusFilter}
                          onChange={setStatusFilter}
                          options={STATUS_OPTIONS}
                          styles={dropdownStyles}
                          placeholder="All statuses"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>
                    <div className="col-xl-4 col-lg-4 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={riderFilter}
                          onChange={setRiderFilter}
                          options={riderOptions}
                          styles={dropdownStyles}
                          placeholder="All riders"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <PageSizeDropDownSelection
                          defaultValue={pageSize}
                          onChange={setPageSize}
                          styles={dropdownStyles}
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-12">
                      <button
                        type="button"
                        className="btn"
                        onClick={clearFilters}
                        title="Clear all filters"
                        style={{
                          height: "36px",
                          backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.8)" : "rgba(255, 255, 255, 0.2)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          color: "white",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa fa-times me-1"></i>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="table-responsive table-container"
              style={{ minHeight: "200px", maxHeight: "450px", overflowY: "auto", overflowX: "auto", position: "relative", zIndex: 1 }}
            >
              <table className="table table-hover text-nowrap">
                <thead
                  className="sticky-header"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10000,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th className="pt-2 pb-2">ID</th>
                    <th className="pt-2 pb-2">Rider</th>
                    <th className="pt-2 pb-2">Amount</th>
                    <th className="pt-2 pb-2">Status</th>
                    <th className="pt-2 pb-2">Method / Ref</th>
                    <th className="pt-2 pb-2">Created</th>
                    <th className="pt-2 pb-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.length > 0 ? (
                    payouts.map((item) => {
                      const id = item.payoutId || item.Payout_ID;
                      const status = item.status || item.Status;
                      return (
                        <tr key={id}>
                          <td className="align-middle">{id}</td>
                          <td className="align-middle">{item.riderName}</td>
                          <td className="align-middle">{formatMoney(item.amount ?? item.Amount)}</td>
                          <td className="align-middle">
                            <span className={`badge ${statusBadgeClass(status)}`}>{status}</span>
                          </td>
                          <td className="align-middle">{[item.paymentMethod, item.paymentReference].filter(Boolean).join(" / ") || "—"}</td>
                          <td className="align-middle">{item.createdAt ? new Date(item.createdAt).toLocaleString() : "—"}</td>
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {status === "Pending" && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => handleApprove(id)}
                                  title="Approve payout"
                                  style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px" }}
                                >
                                  <i className="fa fa-check" style={{ fontSize: "12px" }} />
                                </button>
                              )}
                              {(status === "Pending" || status === "Approved") && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => {
                                    setMarkPaidPayout(item);
                                    setMarkPaidMethod(item.paymentMethod || "");
                                    setMarkPaidRef(item.paymentReference || "");
                                  }}
                                  title="Mark paid"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "6px",
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                  }}
                                >
                                  <i className="fa fa-money-bill-wave" style={{ fontSize: "12px" }} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="align-middle text-center" style={{ fontSize: "18px" }}>
                        {loading ? "Loading payouts..." : "No payouts found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalRecords={totalRecords}
              pageSize={parseInt(pageSize.value, 10) || 50}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setPageNumber={setPageNumber}
              themeColor={themeColor}
            />
          </div>
        </div>
      </div>

      <CustomModal title="Create Rider Payout" isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} isCentered>
        <div className="mb-2">
          <label className="form-label">Rider</label>
          <select
            className="form-select"
            value={payoutForm.marketplaceRider_ID}
            onChange={(e) => setPayoutForm((f) => ({ ...f, marketplaceRider_ID: e.target.value }))}
          >
            <option value="">Select rider</option>
            {riders.map((r) => {
              const id = r.marketplaceRiderId || r.marketplaceRider_ID;
              return (
                <option key={id} value={id}>
                  {r.name || `${r.firstName || ""} ${r.lastName || ""}`.trim()} — earnings: {formatMoney(r.earningsPayableBalance)}
                </option>
              );
            })}
          </select>
        </div>
        <div className="mb-2">
          <label className="form-label">Amount</label>
          <input type="number" className="form-control" min="0" step="0.01" value={payoutForm.amount} onChange={(e) => setPayoutForm((f) => ({ ...f, amount: e.target.value }))} />
        </div>
        <div className="mb-2">
          <label className="form-label">Payment method</label>
          <input className="form-control" value={payoutForm.paymentMethod} onChange={(e) => setPayoutForm((f) => ({ ...f, paymentMethod: e.target.value }))} placeholder="Bank transfer, JazzCash, etc." />
        </div>
        <div className="mb-2">
          <label className="form-label">Reference</label>
          <input className="form-control" value={payoutForm.paymentReference} onChange={(e) => setPayoutForm((f) => ({ ...f, paymentReference: e.target.value }))} />
        </div>
        <div className="mb-3">
          <label className="form-label">Notes</label>
          <textarea className="form-control" rows={2} value={payoutForm.notes} onChange={(e) => setPayoutForm((f) => ({ ...f, notes: e.target.value }))} />
        </div>
        <button type="button" className="btn btn-primary" onClick={handleCreate}>Create</button>
      </CustomModal>

      <CustomModal title="Mark Payout Paid" isOpen={!!markPaidPayout} onClose={() => setMarkPaidPayout(null)} isCentered>
        {markPaidPayout && (
          <div>
            <p>Amount: <strong>{formatMoney(markPaidPayout.amount)}</strong> for {markPaidPayout.riderName}</p>
            <div className="mb-2">
              <label className="form-label">Payment method</label>
              <input className="form-control" value={markPaidMethod} onChange={(e) => setMarkPaidMethod(e.target.value)} />
            </div>
            <div className="mb-3">
              <label className="form-label">Payment reference</label>
              <input className="form-control" value={markPaidRef} onChange={(e) => setMarkPaidRef(e.target.value)} />
            </div>
            <button type="button" className="btn btn-primary" onClick={handleMarkPaid}>Confirm Paid</button>
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default ManageRiderPayouts;
